import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/dropzone/styles.css";
import "@mantine/notifications/styles.css";
import "@fontsource/noto-sans-armenian/400.css";
import "@fontsource/noto-sans-armenian/600.css";
import "./global.css";
import "./i18n";

import { MantineProvider } from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import { Notifications } from "@mantine/notifications";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { useTranslation } from "react-i18next";
import { BrowserRouter } from "react-router";
import { App } from "./app";
import { AuthProvider } from "./auth/auth";
import { DAYJS_LOCALE, type UiLocale } from "./i18n";
import { theme } from "./theme";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 30_000 },
  },
});

/** Calendars follow the UI language (Monday first, dd.mm.yyyy). */
function Dates({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  const locale = DAYJS_LOCALE[(i18n.language as UiLocale) ?? "hy"] ?? "hy-am";
  return (
    <DatesProvider settings={{ locale, firstDayOfWeek: 1, weekendDays: [0, 6] }}>
      {children}
    </DatesProvider>
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("#root missing");

createRoot(root).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <Notifications position="top-right" />
      <Dates>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <App />
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </Dates>
    </MantineProvider>
  </StrictMode>,
);
